import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Phone, Mail, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  { id: "electrical", name: "Electrical" },
  { id: "plumbing", name: "Plumbing" },
  { id: "hvac", name: "HVAC" },
  { id: "carpentry", name: "Carpentry" },
  { id: "painting", name: "Painting" },
  { id: "cleaning", name: "Cleaning" },
  { id: "janitorial", name: "Janitorial" },
  { id: "handyman", name: "Handyman" },
];

const TIME_SLOTS = [
  { id: "morning", label: "Morning (8:00 - 11:00)" },
  { id: "midday", label: "Midday (11:00 - 14:00)" },
  { id: "afternoon", label: "Afternoon (14:00 - 17:00)" },
  { id: "evening", label: "Evening (17:00 - 20:00)" },
];

const initialForm = {
  name: "",
  email: "",
  phone: "",
  property_type: "residential",
  address: "",
  preferred_date: "",
  time_slot: "morning",
  notes: "",
  company: "",
  consent: false,
};

const inputCls =
  "w-full rounded-lg border border-white/10 bg-[#0A0B0E] px-4 py-3 text-sm text-white placeholder:text-slate-500 outline-none transition-colors focus:border-[#FF2A3B]/60";

export const Booking = ({ service, setService }) => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/bookings`, { ...form, service });
      setConfirmation({ id: data.id, ...form, service });
      setForm(initialForm);
      toast.success("Booking request received. Confirmation sent to your email.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not submit your request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const today = new Date().toISOString().split("T")[0];

  return (
    <section id="booking" className="py-16 sm:py-24 lg:py-32" data-testid="booking-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-2"
          >
            <p className="font-jbmono text-xs sm:text-sm uppercase tracking-[0.25em] text-[#FF2A3B] font-semibold" data-testid="booking-eyebrow">
              ✦ Request Service
            </p>
            <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight" data-testid="booking-heading">
              Book a maintenance visit.
            </h2>
            <p className="mt-4 text-base text-slate-400 leading-relaxed">
              Pick a service, a property window, and you're on the board. We review
              every request personally and confirm your estimate before any work
              begins — no surprises.
            </p>

            <div className="mt-10 space-y-5">
              <a href="tel:+15137205882" className="flex items-center gap-4 text-slate-300 transition-colors hover:text-white" data-testid="booking-phone-link">
                <span className="flex h-11 w-11 items-center justify-center rounded-full border border-[#FF2A3B]/40 bg-[#FF2A3B]/10">
                  <Phone size={16} className="text-[#FF2A3B]" />
                </span>
                <span>
                  <span className="block font-jbmono text-xs uppercase tracking-[0.18em] text-slate-500">Dispatch Line</span>
                  (513) 720-5882
                </span>
              </a>
              <a href="mailto:redstarsolutions513@gmail.com" className="flex items-center gap-4 text-slate-300 transition-colors hover:text-white" data-testid="booking-email-link">
                <span className="flex h-11 w-11 items-center justify-center rounded-full border border-[#FF2A3B]/40 bg-[#FF2A3B]/10">
                  <Mail size={16} className="text-[#FF2A3B]" />
                </span>
                <span>
                  <span className="block font-jbmono text-xs uppercase tracking-[0.18em] text-slate-500">Email</span>
                  redstarsolutions513@gmail.com
                </span>
              </a>
              <div className="flex items-center gap-4 text-slate-300" data-testid="booking-hours-info">
                <span className="flex h-11 w-11 items-center justify-center rounded-full border border-[#FF2A3B]/40 bg-[#FF2A3B]/10">
                  <Clock size={16} className="text-[#FF2A3B]" />
                </span>
                <span>
                  <span className="block font-jbmono text-xs uppercase tracking-[0.18em] text-slate-500">Office Hours</span>
                  Monday–Saturday, 7:00 AM–7:00 PM
                </span>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-3"
          >
            {confirmation ? (
              <div className="card-surface rounded-xl p-10 text-center" data-testid="booking-confirmation">
                <CheckCircle2 size={48} className="mx-auto text-[#10B981]" />
                <h3 className="mt-6 font-display text-2xl font-bold">Request received.</h3>
                <p className="mt-3 text-slate-400">
                  Reference{" "}
                  <span className="font-jbmono text-[#FF2A3B]" data-testid="booking-reference">
                    {confirmation.id.slice(0, 8).toUpperCase()}
                  </span>{" "}
                  — a confirmation email is on its way to {confirmation.email}. We'll
                  review your request and call with your estimate and confirmed window.
                </p>
                <button
                  onClick={() => setConfirmation(null)}
                  className="mt-8 rounded-full border border-white/15 px-6 py-3 text-sm font-semibold text-slate-200 transition-colors hover:border-[#FF2A3B]/60"
                  data-testid="booking-another-button"
                >
                  Book another service
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="card-surface rounded-xl p-6 sm:p-8 space-y-6" data-testid="booking-request-form">
                <div>
                  <label className="mb-3 block font-jbmono text-xs uppercase tracking-[0.18em] text-slate-400">
                    01 — Select service
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="booking-service-picker">
                    {SERVICE_OPTIONS.map((s) => (
                      <button
                        type="button"
                        key={s.id}
                        onClick={() => setService(s.id)}
                        className={`flex items-center justify-center rounded-lg border px-3 py-3 text-sm font-medium transition-all ${
                          service === s.id
                            ? "border-[#FF2A3B] bg-[#FF2A3B]/10 text-white"
                            : "border-white/10 bg-[#0A0B0E] text-slate-400 hover:border-white/25"
                        }`}
                        data-testid={`service-option-${s.id}`}
                      >
                        {s.name}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="mb-3 block font-jbmono text-xs uppercase tracking-[0.18em] text-slate-400">
                    02 — Property type
                  </label>
                  <div className="flex gap-3" data-testid="booking-property-type-toggle">
                    {["residential", "commercial"].map((t) => (
                      <button
                        type="button"
                        key={t}
                        onClick={() => setForm({ ...form, property_type: t })}
                        className={`flex-1 rounded-lg border px-4 py-3 text-sm font-medium capitalize transition-all ${
                          form.property_type === t
                            ? "border-[#FF2A3B] bg-[#FF2A3B]/10 text-white"
                            : "border-white/10 bg-[#0A0B0E] text-slate-400 hover:border-white/25"
                        }`}
                        data-testid={`property-type-${t}`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="mb-2 block font-jbmono text-xs uppercase tracking-[0.18em] text-slate-400" htmlFor="bk-date">
                      03 — Preferred date <span className="text-[#FF2A3B]">*</span>
                    </label>
                    <input
                      id="bk-date"
                      type="date"
                      required
                      min={today}
                      value={form.preferred_date}
                      onChange={set("preferred_date")}
                      className={inputCls}
                      data-testid="booking-preferred-date"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block font-jbmono text-xs uppercase tracking-[0.18em] text-slate-400" htmlFor="bk-slot">
                      Time window
                    </label>
                    <select
                      id="bk-slot"
                      value={form.time_slot}
                      onChange={set("time_slot")}
                      className={inputCls}
                      data-testid="booking-time-slot-select"
                    >
                      {TIME_SLOTS.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input required placeholder="Full name *" value={form.name} onChange={set("name")} className={inputCls} data-testid="booking-name-input" />
                  <input required type="email" placeholder="Email address *" value={form.email} onChange={set("email")} className={inputCls} data-testid="booking-email-input" />
                  <input required type="tel" placeholder="Phone number *" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="booking-phone-input" />
                  <input required placeholder="Property address *" value={form.address} onChange={set("address")} className={inputCls} data-testid="booking-address-input" />
                </div>

                <textarea
                  placeholder="Describe the issue (optional) — leaks, faults, rooms involved..."
                  value={form.notes}
                  onChange={set("notes")}
                  rows={3}
                  className={`${inputCls} resize-none`}
                  data-testid="booking-notes-input"
                />

                <input
                  type="text"
                  name="company"
                  value={form.company}
                  onChange={set("company")}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="pointer-events-none absolute -left-[9999px] h-px w-px opacity-0"
                  data-testid="booking-honeypot-field"
                />

                <label className="flex items-start gap-3 text-sm text-slate-400" data-testid="booking-consent-label">
                  <input
                    type="checkbox"
                    required
                    checked={form.consent}
                    onChange={(e) => setForm({ ...form, consent: e.target.checked })}
                    className="mt-0.5 h-4 w-4 shrink-0 accent-[#FF2A3B]"
                    data-testid="booking-consent-checkbox"
                  />
                  <span>
                    I agree to be contacted by Red Star Solutions about this request,
                    and I understand my details are used only to respond to it. <span className="text-[#FF2A3B]">*</span>
                  </span>
                </label>

                <button
                  type="submit"
                  disabled={submitting}
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-[#FF2A3B] py-4 text-sm font-semibold text-white transition-all hover:bg-[#E01F30] hover:shadow-[0_0_40px_rgba(255,42,59,0.4)] disabled:opacity-60"
                  data-testid="booking-submit-button"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Sending request...
                    </>
                  ) : (
                    "Request Free Estimate"
                  )}
                </button>
                <p className="text-center text-xs text-slate-500 leading-relaxed" data-testid="booking-privacy-notice">
                  Privacy: your details are used only to handle your request — never
                  sold, rented, or shared. All fields except notes are required.
                </p>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
